import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // status: "loading" | "authenticated" | "guest"
    const [status, setStatus] = useState("loading");
    const [user, setUser] = useState(null);

    const rehydrate = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setStatus("guest");
            setUser(null);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setStatus("authenticated");
        } catch {
            setToken(null);
            setUser(null);
            setStatus("guest");
        }
    }, []);

    useEffect(() => {
        rehydrate();
    }, [rehydrate]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setToken(data.token);
        setUser(data.user);
        setStatus("authenticated");
        return data.user;
    }, []);

    const register = useCallback(async (first_name, email, password) => {
        const { data } = await api.post("/auth/register", { first_name, email, password });
        setToken(data.token);
        setUser(data.user);
        setStatus("authenticated");
        return data.user;
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setStatus("guest");
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            return null;
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{ status, user, login, register, logout, refreshUser, formatApiError }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
